import Seo from "components/Seo";
import "assets/styles/TermsServices.css";
import AnimationPage from "components/AnimationPage";
import { motion } from "framer-motion";
import animations from "utils/animations";

export default function TermsServices() {
  const { opacity, opacityAndSlide } = animations.components;

  return (
    <AnimationPage>
      <Seo title="Terms of Services - Kollectiff" />
      <main>
        <div className="container terms-services">

          <motion.h1
            className="k-title-sm md:k-title-md lg:k-title-lg"
            {...opacity}
            transition={{ delay: 0.2 }}
          >Terms of Services
          </motion.h1>
              
          <div className="terms-body">
            <motion.div 
              {...opacityAndSlide}
              transition={{ delay: .3 }}
              className="terms-block">
              <p className="k-title-xxs"><b>Effective Date:</b> Jan 1st, 2025</p>
              <p className="k-text-md">These Terms of Service (the “<b>Terms</b>”) govern the provision of creative-technology, generative-AI and related professional services by <b>The Motiff Group, Inc.</b> (“<b>Kollectiff</b>”, “<b>we</b>”, “<b>us</b>”, or “<b>our</b>”) to the entity that accepts these Terms (the “<b>Client</b>” or “<b>you</b>”). By executing a statement of work, purchase order or other ordering document that references these Terms (an “<b>SOW</b>”), or by otherwise using the Services, Client agrees to be bound by these Terms.</p>
            </motion.div> 


            <motion.div 
              {...opacityAndSlide}
              transition={{ delay: .3 }}
              className="terms-block">
              <h2 className="k-title-xs lg:k-title-sm">1. Definitions</h2>
              <ul>
                <li><b>“Affiliate”</b> means, with respect to a party, any entity that directly or indirectly controls, is controlled by, or is under common control with that party.</li>
                <li><b>“AI Model”</b> means any machine-learning, large-language-model or other generative artificial-intelligence system owned or licensed by Kollectiff and used to produce Generative Output.</li>
                <li><b>“Client Materials”</b> means any materials, data, images, video, audio, text, brand assets, trademarks, trade names, or other content that Client or its Affiliates provide to Kollectiff in connection with the Services, including Training Data supplied by Client.</li>
                <li><b>“Confidential Information”</b> has the meaning set forth in Section 8.</li>
                <li><b>“Deliverables”</b> means all work products delivered to Client under an SOW, including Generative Output.</li>
                <li><b>“Generative Output”</b> means any text, images, audio, video, software code, data or other content that is generated by an AI Model as part of the Services.</li>
                <li><b>“Kollectiff Technology”</b> means the Services, the AI Models, software, tools, code, designs, inventions, know-how, algorithms, workflows, processes and any other technology owned or developed by Kollectiff prior to or independent of an SOW, together with all improvements and derivatives thereof (excluding Client Materials and Deliverables).</li>
                <li><b>“Services”</b> means the professional, creative, generative-AI and other services provided by Kollectiff to Client as described in an SOW.</li>
                <li><b>“Training Data”</b> means data used to train, fine-tune or otherwise adjust an AI Model.</li>
              </ul>
            </motion.div> 

            <motion.div 
              {...opacityAndSlide}
              transition={{ delay: .3 }}
              className="terms-block">
              <h2 className="k-title-xs lg:k-title-sm">2. Scope of Services</h2>
              <p className="k-text-md">Kollectiff will perform the Services and provide the Deliverables described in each SOW in a professional and workmanlike manner consistent with industry standards. Each SOW is hereby incorporated by reference into these Terms. In case of conflict, the SOW will control.</p>
            </motion.div> 

            <motion.div 
              {...opacityAndSlide}
              transition={{ delay: .3 }}
              className="terms-block">
              <h2 className="k-title-xs lg:k-title-sm">3. License and Ownership</h2>

              <div className="terms-text">
                <h3 className="k-title-xxs">3.1 Client Materials</h3>
                <p className="k-text-md">Client retains all right, title and interest in and to Client Materials. Client hereby grants Kollectiff a non-exclusive, royalty-free, worldwide license to use, reproduce, modify, distribute and display Client Materials solely as necessary to perform the Services and produce the Deliverables.</p>
              </div>

              <div className="terms-text">
                <h3 className="k-title-xxs">3.2 Generative Output and Deliverables</h3>
                <p className="k-text-md">Upon Client’s full payment of all fees due under the applicable SOW, Kollectiff hereby <b>assigns</b> to Client all right, title and interest worldwide in and to the Deliverables (including Generative Output), except that Kollectiff retains ownership of Kollectiff Technology incorporated therein. Kollectiff grants Client a perpetual, irrevocable, worldwide, royalty-free license to use Kollectiff Technology solely as embedded in the Deliverables for Client’s internal business purposes and commercial exploitation of the Deliverables.</p>
              </div>

              <div className="terms-text">
                <h3 className="k-title-xxs">3.3 Kollectiff Technology</h3>
                <p className="k-text-md">Except for the limited license in Section 3.2, Kollectiff retains all right, title and interest in and to Kollectiff Technology and all related intellectual-property rights.</p>
              </div>

              <div className="terms-text">
                <h3 className="k-title-xxs">3.4 Feedback</h3>
                <p className="k-text-md">Client may provide comments or suggestions regarding the Services (“<b>Feedback</b>”). Client grants Kollectiff a perpetual, irrevocable, worldwide, royalty-free license to use and incorporate Feedback into Kollectiff Technology without obligation and at its sole discretion.</p>
              </div>

              <div className="terms-text">
                <h3 className="k-title-xxs">3.5 AI Training Restrictions</h3>
                <p className="k-text-md">Kollectiff will <b>not</b> use Client Materials or Deliverables to train or fine‑tune AI Models for the benefit of Kollectiff or third parties without Client’s prior written consent, except to the extent necessary to provide the Services to Client.</p>
              </div>

            </motion.div> 

            <motion.div 
              {...opacityAndSlide}
              transition={{ delay: .3 }}
              className="terms-block">
              <h2 className="k-title-xs lg:k-title-sm">4. Acceptable-Use Policy (AUP)</h2>

              <div className="terms-text">
                <p className="k-text-md">Client shall not, and shall not permit any third party to:</p>
                <ol>
                  <li>use the Services or Deliverables in violation of any law, regulation or third-party right;</li>
                  <li>attempt to reverse-engineer, decompile or extract the weights, architecture or Training Data of any AI Model;</li>
                  <li>submit Client Materials that are infringing, defamatory, harassing, obscene or otherwise unlawful;</li>
                  <li>use the Services to create content that facilitates the planning or execution of violence, terrorism or other illegal activity;</li>
                  <li>remove or alter proprietary notices on any Deliverable or Kollectiff Technology;</li>
                  <li>use the Services or Deliverables to train a competing AI model or dataset. Kollectiff may suspend Services immediately upon notice for Client’s material violation of this AUP.</li>
                </ol>
              </div>

            </motion.div> 

            <motion.div 
              {...opacityAndSlide}
              transition={{ delay: .3 }}
              className="terms-block">
              <h2 className="k-title-xs lg:k-title-sm">5. Data Privacy & Security</h2>
              <p className="k-text-md">Kollectiff will implement and maintain administrative, physical and technical safeguards designed to protect Client Materials against unauthorized access, loss or disclosure. Additional data-processing obligations may be set forth in a separate Data-Processing Addendum (“DPA”), which, upon execution, will be incorporated into these Terms. In the event of a Personal-Data Breach involving Client Materials, Kollectiff will notify Client without undue delay and cooperate in remediation. Kollectiff agrees that Client’s data shall be used to (i) provide, maintain and improve the Services, (ii) process transactions and communicate with the Client, and (iii) comply with legal obligations.</p>
            </motion.div> 

            <motion.div 
              {...opacityAndSlide}
              transition={{ delay: .3 }}
              className="terms-block">
              <h2 className="k-title-xs lg:k-title-sm">6. Fees and Payment</h2>
              <p className="k-text-md">Fees, expenses, invoicing milestones and payment terms are specified in each SOW. Late payments accrue interest at 1.5 % per month or the maximum rate permitted by law, whichever is lower. Client is responsible for all taxes, duties and government charges except taxes based on Kollectiff’s net income.</p>
            </motion.div> 

            <motion.div 
              {...opacityAndSlide}
              transition={{ delay: .3 }}
              className="terms-block">
              <h2 className="k-title-xs lg:k-title-sm">7. Warranties and Disclaimers</h2>

              <div className="terms-text">
                <h3 className="k-title-xxs">7.1 Mutual Warranties</h3>
                <p className="k-text-md">Each party represents that it has the power and authority to enter into these Terms and that its performance will not violate any other agreement.</p>
              </div>

              <div className="terms-text">
                <h3 className="k-title-xxs">7.2 Kollectiff Warranties</h3>
                <p className="k-text-md">Kollectiff warrants that: (a) it will perform the Services in a professional and workmanlike manner; and (b) the Deliverables, as delivered, will not knowingly infringe any third‑party intellectual‑property rights.</p>
              </div>

              <div className="terms-text">
                <h3 className="k-title-xxs">7.3 AI-Specific Disclaimer</h3>
                <p className="k-text-md"><b>AI technology is probabilistic and may generate inaccurate, biased or incomplete outputs. Client is solely responsible for reviewing and approving all Deliverables before use or publication.</b> EXCEPT AS EXPRESSLY STATED IN THIS SECTION 7, THE SERVICES AND DELIVERABLES ARE PROVIDED “AS IS” AND KOLLECTIFF DISCLAIMS ALL OTHER WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NON-INFRINGEMENT.</p>
              </div>

            </motion.div> 

            <motion.div 
              {...opacityAndSlide}
              transition={{ delay: .3 }}
              className="terms-block">
              <h2 className="k-title-xs lg:k-title-sm">8. Indemnification</h2>

              <div className="terms-text">
                <h3 className="k-title-xxs">8.1 Kollectiff Indemnity</h3>
                <p className="k-text-md">Kollectiff will defend Client against any third-party claim that the Deliverables (excluding Client Materials and modifications made by Client) infringe a U.S. patent, copyright or trademark, and will pay resulting damages finally awarded or agreed in settlement to the limit of the fees agreed and paid in each SOW, provided Client promptly notifies Kollectiff and grants sole control of the defense. If a Deliverable is held to infringe, Kollectiff may obtain a license, modify the Deliverable or refund the infringing portion of fees.</p>
              </div>

              <div className="terms-text">
                <h3 className="k-title-xxs">8.2 Client Indemnity</h3>
                <p className="k-text-md">Client will defend Kollectiff against any third-party claim arising from (a) Client Materials, (b) Client’s use of the Deliverables in violation of the AUP or law, or (c) any outputs generated from prompts supplied by Client, and will pay resulting damages without limitation.</p>
              </div>

            </motion.div> 

            <motion.div 
              {...opacityAndSlide}
              transition={{ delay: .3 }}
              className="terms-block">
              <h2 className="k-title-xs lg:k-title-sm">9. Limitation of Liability</h2>
              <p className="k-text-md">EXCEPT FOR (i) GROSS NEGLIGENCE OR WILLFUL MISCONDUCT, (ii) INDEMNITY OBLIGATIONS, OR (iii) BREACH OF CONFIDENTIALITY, KOLLECTIFF WILL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL OR PUNITIVE DAMAGES, OR FOR LOST PROFITS OR REVENUES. KOLLECTIFF’S TOTAL LIABILITY UNDER THESE TERMS WILL NOT EXCEED THE FEES PAID OR PAYABLE BY CLIENT UNDER THE SOW GIVING RISE TO THE CLAIM DURING <b>THE TWELVE (12) MONTHS</b> PRECEDING THE EVENT.</p>
            </motion.div> 

            <motion.div 
              {...opacityAndSlide}
              transition={{ delay: .3 }}
              className="terms-block">
              <h2 className="k-title-xs lg:k-title-sm">10. Confidentiality</h2>

              <div className="terms-text">
                <h3 className="k-title-xxs">10.1 Definition</h3>
                <p className="k-text-md"><b>“Confidential Information”</b> means any non-public business, technical or financial information disclosed by one party (“<b>Disclosing Party</b>”) to the other (“<b>Receiving Party</b>”) that is marked or identified as confidential or that a reasonable person would understand to be confidential.</p>
              </div>

              <div className="terms-text">
                <h3 className="k-title-xxs">10.2 Obligations</h3>
                <p className="k-text-md">Receiving Party will use Confidential Information solely to perform under these Terms, protect it with reasonable care, and not disclose it to any third party except to its employees, contractors or advisors bound by similar obligations. These obligations survive for five (5) years after termination (perpetually for trade secrets).</p>
              </div>

            </motion.div> 

            <motion.div 
              {...opacityAndSlide}
              transition={{ delay: .3 }}
              className="terms-block">
              <h2 className="k-title-xs lg:k-title-sm">11. Term and Termination</h2>
              <p className="k-text-md">These Terms commence on the Effective Date and continue until terminated as set forth herein. Either party may terminate an SOW for material breach if the breaching party fails to cure within thirty (30) days after written notice. Either party may terminate these Terms and all SOWs (i) upon thirty (30) days’ written notice if no SOW is then active, or (ii) immediately if the other party becomes insolvent or enters bankruptcy.</p>
              <p className="k-text-md">Upon termination, Client will pay all fees accrued as of the termination date. Sections 1, 3, 4, 5, 7.3, 8-10, 12 and 13 will survive termination.</p>
            </motion.div> 

            <motion.div 
              {...opacityAndSlide}
              transition={{ delay: .3 }}
              className="terms-block">
              <h2 className="k-title-xs lg:k-title-sm">12. Compliance With Laws; Export Control</h2>
              <p className="k-text-md">Each party will comply with all applicable laws and regulations, including export‑control laws. Client represents it is not named on any U.S. government denied‑party list. Client will not allow access to the Services or Deliverables in violation of such laws.</p>
            </motion.div> 

            <motion.div 
              {...opacityAndSlide}
              transition={{ delay: .3 }}
              className="terms-block">
              <h2 className="k-title-xs lg:k-title-sm">13. Miscellaneous</h2>
              <ul>
                <li><b>Governing Law; Venue.</b> These Terms and any disputes arising hereunder are governed by the laws of the State of Delaware (excluding its conflict-of-laws rules). The parties consent to exclusive jurisdiction in the state and federal courts located in Delaware.</li>
                <li><b>Independent Contractors.</b> The parties are independent contractors. These Terms do not create a partnership, joint venture, or agency relationship.</li>
                <li><b>Assignment.</b> Neither party may assign these Terms without the other’s prior written consent, except to an Affiliate or in connection with a merger, acquisition or sale of substantially all assets.</li>
                <li><b>Force Majeure.</b> Neither party is liable for failure to perform due to events beyond its reasonable control.</li>
                <li><b>Notices.</b> Notices must be in writing and deemed given upon receipt at the addresses set forth in the applicable SOW (or as updated).</li>
                <li><b>Amendments.</b> These Terms may be amended only by a written agreement signed by both parties.</li>
                <li><b>Order of Precedence.</b> If there is a conflict between these Terms, an SOW, or a DPA, the following order will apply: (1) DPA, (2) SOW, (3) these Terms.</li>
                <li><b>Severability.</b> If any provision is held unenforceable, the remainder will remain in full force..</li>
                <li><b>Entire Agreement.</b> These Terms, together with all SOWs and DPAs, constitute the entire agreement between the parties regarding the subject matter and supersede all prior or contemporaneous agreements or representations.</li>
              </ul>
            </motion.div> 
            <motion.div 
              {...opacityAndSlide}
              transition={{ delay: .3 }}
              className="terms-block">
              <p className="k-text-md">© 2025 The Motiff Group, Inc. All rights reserved.</p>
            </motion.div>
          </div>

        </div>
      </main>
    </AnimationPage>
  );
}
